if (!customElements.get('featured-collection')) {
  customElements.define(
    'featured-collection',
    class FeaturedCollection extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items);
        this.tabletItems = parseInt(this.dataset.tabletItems);

        this.sliderInstance = false;

        if (!this.enableSlider) return;

        if (this.sliderWrapper) {
          this._sliderWrapperDefaultClass = this.sliderWrapper.className;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);
        this.init();
      }

      init() {
        const isBmCollection = this.section && this.section.classList.contains('bm-featured-collection');
        const isLandingFc = this.section && this.section.classList.contains('bm-landing-featured-collection');
        const useBmSlider = isBmCollection || isLandingFc;
        /*
         * Sleek defaults: destroy Swiper on small viewports and use `.swipe-mobile` native scroll.
         * BM featured strip must match Figma: real Swiper + bar pagination on mobile, so we always
         * init here and strip `swipe-mobile*` classes inside `initSlider` to avoid grid/scroll conflicts.
         */
        if (useBmSlider || !FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          this.destroySlider();
        }

        /*
         * BM-only: on mobile, the "Shop all" CTA lives under the pagination bar (Figma).
         * We relocate the existing `.section__header--button` node at runtime so the desktop
         * header row (`show_all_button_on_top: true`) stays identical — no markup changes.
         */
        if (isBmCollection) this.handleBmMobileButtonPosition();
      }

      handleBmMobileButtonPosition() {
        if (!this.section) return;
        const headerButton = this.section.querySelector('.section__header--button');
        if (!headerButton) return;

        if (!this._headerButtonPlaceholder) {
          this._headerButtonPlaceholder = document.createComment('bm-header-button-placeholder');
          headerButton.parentNode.insertBefore(this._headerButtonPlaceholder, headerButton);
        }

        const isMobile = FoxTheme.config.mqlMobile;
        const pagination = this.section.querySelector('.swiper-pagination');

        if (isMobile && pagination && pagination.parentNode) {
          if (headerButton.previousSibling !== pagination) {
            pagination.parentNode.insertBefore(headerButton, pagination.nextSibling);
          }
          headerButton.classList.add('section__header--button--bm-mobile');
        } else if (this._headerButtonPlaceholder && this._headerButtonPlaceholder.parentNode) {
          if (headerButton.nextSibling !== this._headerButtonPlaceholder) {
            this._headerButtonPlaceholder.parentNode.insertBefore(headerButton, this._headerButtonPlaceholder);
          }
          headerButton.classList.remove('section__header--button--bm-mobile');
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;

        const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);
        const isBmCollection = this.section && this.section.classList.contains('bm-featured-collection');
        const isLandingFc = this.section && this.section.classList.contains('bm-landing-featured-collection');
        const useBmSlider = isBmCollection || isLandingFc;
        const bmSlideGap = isLandingFc ? 12 : 10;

        if (useBmSlider) {
          [...this.classList]
            .filter((c) => c === 'swipe-mobile' || c.startsWith('swipe-mobile--'))
            .forEach((c) => this.classList.remove(c));
          if (this.sliderWrapper) {
            this.sliderWrapper.classList.remove('swipe-mobile__inner');
          }
        }

        FoxTheme.utils.stripTailwindGridUtilities(this.sliderWrapper);

        this.sliderOptions = {
          slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
          spaceBetween: columnGap.tablet,
          navigation: {
            nextEl: this.section.querySelector(this.selectors.nextEl),
            prevEl: this.section.querySelector(this.selectors.prevEl),
          },
          pagination: {
            el: this.section.querySelector('.swiper-pagination'),
            clickable: true,
          },
          breakpoints: {
            1024: {
              slidesPerView: this.items > 4 ? 4 : parseInt(this.items),
              spaceBetween: columnGap.tabletLarge,
            },
            1280: {
              slidesPerView: parseInt(this.items),
              spaceBetween: columnGap.desktop,
            },
          },
          loop: true,
          threshold: 2,
        };

        if (useBmSlider) {
          this.sliderOptions.slidesPerView = 'auto';
          this.sliderOptions.spaceBetween = bmSlideGap;
          /*
           * Mobile (< 768): Figma shows the active 330px card centered with equal left/right
           * peek. `centeredSlides: true` is the base; tablet/desktop breakpoints explicitly
           * opt out so the desktop grid-style strip stays untouched.
           */
          this.sliderOptions.centeredSlides = true;
          this.sliderOptions.breakpoints = {
            768: { slidesPerView: 'auto', spaceBetween: bmSlideGap, centeredSlides: false },
            1024: { slidesPerView: 'auto', spaceBetween: bmSlideGap, centeredSlides: false },
            1280: { slidesPerView: 'auto', spaceBetween: bmSlideGap, centeredSlides: false },
          };
          this.sliderOptions.autoplay = {
            delay: 4000,
            disableOnInteraction: false,
          };
          if (isLandingFc) {
            this.sliderOptions.loop = false;
          }
        }

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        if (useBmSlider) {
          /*
           * `f-grid--gap-*` on `<featured-collection>` only sets gap CSS variables but can interact
           * oddly with Swiper’s flex host; strip it so the carousel root is a plain block.
           */
          [...this.classList].forEach((c) => {
            if (c.startsWith('f-grid--gap-') || c.startsWith('f-grid--row-gap')) {
              this.classList.remove(c);
            }
          });
        }

        const extraModules = useBmSlider ? [FoxTheme.Swiper.Autoplay] : null;
        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, extraModules);
        this.sliderInstance.init();

        if (useBmSlider && this.sliderInstance?.slider) {
          /*
           * BM uses `slidesPerView: 'auto'` + fixed slide widths. If Swiper initialises while the
           * host width is still unstable, `update()` after layout avoids a single-card / blank rail.
           */
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              try {
                this.sliderInstance.slider.update();
              } catch (e) {
                /* ignore */
              }
            });
          });
        }

        this.fixQuickviewDuplicate();

        this.calcNavButtonsPosition();

        window.addEventListener("resize", FoxTheme.utils.debounce(() => {
          this.calcNavButtonsPosition();
        }, 100));
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        if (typeof this.sliderInstance === 'object') {
          try {
            this.sliderInstance.slider.destroy(true, true);
          } catch (e) {
            try {
              this.sliderInstance.slider.destroy();
            } catch (e2) {
              console.error('[featured-collection] Swiper destroy failed', {
                section_id: this.sectionId,
                error: e2,
                first_error: e,
              });
            }
          }
          this.sliderInstance = false;
        }
        if (this._sliderWrapperDefaultClass) {
          this.sliderWrapper.className = this._sliderWrapperDefaultClass;
        } else {
          this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
          this.sliderWrapper.classList.add(this.classes.grid);
        }
      }

      calcNavButtonsPosition() {
        if (this.dataset.calcButtonPosition !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty(
            '--swiper-navigation-top-offset',
            parseInt(firstMedia.clientHeight) / 2 + 'px'
          );
        }
      }

      calcHeadingColumnHeight() {
        if (this.dataset.calcHeadingColumnHeight !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty('--heading-column-height', firstMedia.clientHeight + 'px');
        }
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }
    }
  );
}
